import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useBladeStack } from './BladeStackProvider';
import { encodeStack, decodeStack } from './url';

// Bidirectional blade-stack ⇄ URL sync (HUB-275 deep-link). The stack is the
// source of truth for in-app navigation; the URL mirrors it (shareable / refresh-
// safe), and external URL changes (manual edit, back/forward, a shared link)
// hydrate the stack.
//
// Both directions guard on the *encoding* (key/entityId/mode/active — the only
// navigable identity the codec carries), so they can't ping-pong: once the URL
// equals encodeStack(state), neither effect fires. Titles/params aren't encoded,
// so they never trigger a spurious navigation.
//
// The app router uses basename="/workbench", so location.pathname is already the
// codec's `/w/...` form.
export function useBladeStackUrl(): void {
  const { state, dispatch } = useBladeStack();
  const location = useLocation();
  const navigate = useNavigate();

  const encoded = encodeStack(state);
  const current = `${location.pathname}${location.search}`;

  // state → URL: mirror the stack into the address bar (replace, no history spam).
  useEffect(() => {
    if (encoded !== current) navigate(encoded, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [encoded]);

  // URL → state: hydrate when the address bar describes a different stack.
  useEffect(() => {
    const decoded = decodeStack(location.pathname, location.search);
    if (encodeStack(decoded) !== encoded) dispatch({ type: 'hydrate', state: decoded });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);
}
